import React, { useEffect, useState } from 'react';
import Login from './Login';
import Charts from './Charts';
import Filters from './Filters';
import Loading from './Loading';
import ExportButton from './ExportButton';
import Calendar from './Calendar';
import Customers from './Customers';
import Vehicles from './Vehicles';
import Services from './Services';
import Settings from './Settings';
import AdminBilling from './AdminBilling';
import AdminSettings from './AdminSettings';
import JevVeicular from './JevVeicular';
import JevPublicView from './JevPublicView';
import { apiFetch } from './api';
import './index.css';

function App() {
  const [role, setRole] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [view, setView] = useState('dashboard');
  const [sessionLoading, setSessionLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardAppointments, setDashboardAppointments] = useState([]);
  const [jobFilter, setJobFilter] = useState({});
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [isModaling, setIsModaling] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [newImplementationFee, setNewImplementationFee] = useState(0);
  const [newAiApiKey, setNewAiApiKey] = useState('');
  const [newDocument, setNewDocument] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');

  // Checagem de rota pública para a Jornada Eletrônica Veicular do Cliente Final
  const currentPathname = window.location.pathname;
  if (currentPathname.startsWith('/jev/share/')) {
    const publicToken = currentPathname.replace('/jev/share/', '').split('/')[0];
    return <JevPublicView token={publicToken} />;
  }


  useEffect(() => {
    const restoreSession = async () => {
      try {
        const resp = await apiFetch('/auth/me');
        if (!resp.ok) {
          setSessionLoading(false);
          return;
        }
        const data = await resp.json();
        setRole(data.role || null);
        setTenantId(data.tenant_id ?? null);
        setView(data.role === 'super_admin' ? 'super_admin' : 'dashboard');
      } catch (e) {
        console.error('Session restore failed:', e);
      } finally {
        setSessionLoading(false);
      }
    };
    restoreSession();
  }, []);

  useEffect(() => {
    if (view === 'super_admin' && role === 'super_admin') {
      Promise.all([
        apiFetch('/admin/tenants'),
        apiFetch('/admin/stats')
      ])
      .then(async ([tRes, sRes]) => {
        const [tData, sData] = await Promise.all([tRes.json(), sRes.json()]);
        setTenants(tData.tenants || []);
        setGlobalStats(sData);
      });
    }
  }, [view, role]);

  const createTenant = async () => {
    try {
      const resp = await apiFetch('/admin/tenants', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newTenantName,
          implementation_fee: Number(newImplementationFee || 0),
          ai_api_key: newAiApiKey || null,
          document: newDocument || null,
          phone: newPhone || null,
          address: newAddress || null
        })
      });
      if (resp.ok) {
        setNewTenantName('');
        setNewImplementationFee(0);
        setNewAiApiKey('');
        setNewDocument('');
        setNewPhone('');
        setNewAddress('');
        setIsModaling(false);
        setView('dashboard'); 
        setTimeout(() => setView('super_admin'), 100);
      }
    } catch (err) { alert('Erro ao criar oficina'); }
  };

  const handleLogin = (data) => {
    setRole(data.role);
    setTenantId(data.tenant_id);
    if (data.role === 'super_admin') {
      setView('super_admin');
    } else {
      setView('dashboard');
    }
  };

  useEffect(() => {
    if (!role || role === 'super_admin') return;
    setLoading(true);
    
    const fetchData = async () => {
      try {
        const [statusRes, dashboardRes, apptRes] = await Promise.all([
          apiFetch('/status'),
          apiFetch(`/dashboard/today?tenant_id=${tenantId}`),
          apiFetch(`/appointments/list?tenant_id=${tenantId}`)
        ]);

        const [statusData, dashData, apptData] = await Promise.all([
          statusRes.json(),
          dashboardRes.json(),
          apptRes.json()
        ]);

        setStatus(statusData);
        setDashboardData(dashData);
        setDashboardAppointments(apptData.appointments || []);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); 
    return () => clearInterval(interval);
  }, [jobFilter, role, tenantId]);

  const handleImpersonate = async (targetTenantId) => {
    try {
      const resp = await apiFetch(`/admin/impersonate/${targetTenantId}`, {
        method: 'POST'
      });
      if (resp.ok) {
        const data = await resp.json();
        setRole(data.role);
        setTenantId(data.tenant_id);
        setView('dashboard');
      } else {
        const err = await resp.json();
        alert(`Erro: ${err.detail || 'Falha ao logar como admin'}`);
      }
    } catch (err) { 
      alert('Erro na conexão com o servidor'); 
    }
  };

  const handleToggleTenantStatus = async (targetTenantId, currentStatus) => {
    try {
      const resp = await apiFetch(`/admin/tenants/${targetTenantId}/status?active=${!currentStatus}`, {
        method: 'POST'
      });
      if (resp.ok) {
        setTenants(prev => prev.map(t => t[0] === targetTenantId ? [t[0], t[1], t[2], !currentStatus] : t));
      } else {
        alert('Erro ao alterar status no servidor');
      }
    } catch (err) { 
      alert('Erro ao alterar status'); 
    }
  };


  if (sessionLoading) return <Loading />;
  if (!role) return <Login onLogin={handleLogin} />;
  if (loading && !status && role !== 'super_admin') return <Loading />;

  const renderModule = () => {
    switch (view) {
      case 'dashboard':
        const today = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
        const stats = dashboardData?.stats || { appointments_today: 0, in_progress: 0, completed: 0, notifications_today: 0 };
        const timeline = dashboardData?.timeline || [];
        const todayAppointments = dashboardAppointments.filter(apt => new Date(apt[4]).toDateString() === new Date().toDateString());

        return (
          <div className="animate-fade">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <div>
                <h1 style={{ margin: 0, fontSize: '1.8rem', color: 'var(--text-primary)' }}>Dashboard</h1>
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Bem-vindo de volta! Aqui está o resumo de hoje.</p>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="stat-card" style={{ padding: '0.6rem 1.2rem', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                  📅 <b>Hoje, {today}</b>
                </div>
                <button className="btn-primary" onClick={() => setView('calendar')} style={{ background: '#10b981', border: 'none', borderRadius: '8px' }}>+ Novo Agendamento</button>
              </div>
            </div>

            <div className="stats-grid">
              <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
                <div className="stat-icon" style={{ background: '#e0f2fe', color: '#0ea5e9', margin: 0 }}>📅</div>
                <div>
                  <div className="stat-value" style={{ margin: 0, lineHeight: 1.1 }}>{stats.appointments_today}</div>
                  <div className="stat-label">Agendamentos Hoje</div>
                </div>
              </div>
              <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
                <div className="stat-icon" style={{ background: '#fef3c7', color: '#d97706', margin: 0 }}>🕒</div>
                <div>
                  <div className="stat-value" style={{ margin: 0, lineHeight: 1.1 }}>{stats.in_progress}</div>
                  <div className="stat-label">Em Andamento</div>
                </div>
              </div>
              <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
                <div className="stat-icon" style={{ background: '#dcfce7', color: '#10b981', margin: 0 }}>✅</div>
                <div>
                  <div className="stat-value" style={{ margin: 0, lineHeight: 1.1 }}>{stats.completed}</div>
                  <div className="stat-label">Concluídos</div>
                </div>
              </div>
              <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
                <div className="stat-icon" style={{ background: '#f3e8ff', color: '#9333ea', margin: 0 }}>💬</div>
                <div>
                  <div className="stat-value" style={{ margin: 0, lineHeight: 1.1 }}>{stats.notifications_today}</div>
                  <div className="stat-label">Notificações Enviadas</div>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginTop: '2rem' }}>
              <div className="table-wrapper" style={{ margin: 0, padding: '1.5rem', borderRadius: '12px' }}>
                <h3 style={{ fontSize: '1.2rem', color: 'var(--text-primary)', marginBottom: '1.5rem' }}>📋 Agendamentos de Hoje</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Horário</th>
                      <th>Elevador (Box)</th>
                      <th>Mecânico</th>
                      <th>Cliente</th>
                      <th>Veículo</th>
                      <th>Serviço</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todayAppointments.map((apt, idx) => {
                       const start = new Date(apt[4]).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                       const statusColor = apt[7] === 'completed' ? '#10b981' : apt[7] === 'em_andamento' ? '#f59e0b' : '#38bdf8';
                       const statusBg = apt[7] === 'completed' ? '#dcfce7' : apt[7] === 'em_andamento' ? '#fef3c7' : '#e0f2fe';
                       const statusLabel = apt[7] === 'scheduled' ? 'Agendado' : apt[7] === 'completed' ? 'Pronto' : 'Em Andamento';

                       return (
                        <tr key={idx}>
                          <td style={{ fontWeight: '600' }}>{start}</td>
                          <td><span className="logo-circle" style={{ width: '30px', height: '30px', fontSize: '0.8rem' }}>{apt[6]}</span></td>
                          <td style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>{apt[8] || 'Não Alocado'}</td>
                          <td style={{ fontWeight: '600' }}>{apt[1]}</td>
                          <td><span className="logo-circle" style={{ width: 'auto', padding: '2px 8px', borderRadius: '6px', fontSize: '0.8rem', background: 'var(--bg-main)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>{apt[2]}</span></td>
                          <td>{apt[3]}</td>
                          <td>
                            <span style={{ background: statusBg, color: statusColor, padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                              {statusLabel}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                    {todayAppointments.length === 0 && (
                      <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>Nenhum agendamento para hoje.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="table-wrapper" style={{ margin: 0, padding: '1.5rem', borderRadius: '12px' }}>
                <h3 style={{ fontSize: '1.2rem', color: 'var(--text-primary)', marginBottom: '1.5rem' }}>🕒 Linha do Tempo</h3>
                <div style={{ position: 'relative', paddingLeft: '1rem' }}>
                  {timeline.map((item, idx) => {
                    const timeStr = new Date(item.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    const isNotif = item.type === 'notification';
                    const color = isNotif ? '#8b5cf6' : '#10b981';
                    
                    return (
                      <div key={idx} style={{ position: 'relative', paddingBottom: '1.5rem', borderLeft: '2px solid var(--border)', paddingLeft: '1.5rem' }}>
                        <div style={{ position: 'absolute', left: '-6px', top: '0', width: '10px', height: '10px', borderRadius: '50%', background: color }}></div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>{timeStr}</div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{typeof item.detail === 'object' ? JSON.stringify(item.detail) : item.detail}</div>
                      </div>
                    )
                  })}
                  {timeline.length === 0 && (
                    <div style={{ color: 'var(--text-secondary)' }}>Nenhuma atividade hoje.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 'calendar':
        return <Calendar tenantId={tenantId} />;
      case 'customers':
        return <Customers tenantId={tenantId} />;
      case 'vehicles':
        return <Vehicles tenantId={tenantId} />;
      case 'services':
        return <Services tenantId={tenantId} />;
      case 'settings':
        return <Settings tenantId={tenantId} />;
      case 'super_admin':
        return (
          <div className="animate-fade">
             <div className="stats-grid" style={{ marginBottom: '2.5rem' }}>
              <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
                <div className="stat-icon" style={{ background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', margin: 0 }}>🏢</div>
                <div>
                  <div className="stat-value" style={{ margin: 0, lineHeight: 1.1 }}>{globalStats?.total_tenants || 0}</div>
                  <div className="stat-label">Oficinas Ativas (Tenants)</div>
                </div>
              </div>
              <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
                <div className="stat-icon" style={{ background: 'rgba(74, 222, 128, 0.1)', color: '#4ade80', margin: 0 }}>💳</div>
                <div>
                  <div className="stat-value" style={{ margin: 0, lineHeight: 1.1 }}>R$ {(globalStats?.total_tenants * 150).toFixed(2) || '0.00'}</div>
                  <div className="stat-label">MRR Estimado (Recorrente)</div>
                </div>
              </div>
              <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
                <div className="stat-icon" style={{ background: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24', margin: 0 }}>🚀</div>
                <div>
                  <div className="stat-value" style={{ margin: 0, lineHeight: 1.1 }}>{globalStats?.appointments_today || 0}</div>
                  <div className="stat-label">Uso Global (Ações Hoje)</div>
                </div>
              </div>
            </div>

             <div className="stat-card" style={{ padding: '2rem' }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <div>
                    <h2 style={{ color: 'var(--text-primary)' }}>Lista de Inquilinos (Oficinas)</h2>
                    <p className="stat-label">Gestão de acesso e faturamento de clientes da plataforma</p>
                  </div>
                  <button className="btn-primary" onClick={() => setIsModaling(true)}>+ Onboarding de Oficina</button>
               </div>
                 {isModaling && (
                  <div className="table-wrapper animate-fade" style={{ background: 'var(--bg-main)', padding: '2rem', marginBottom: '2rem', border: '1px solid var(--border)' }}>
                     <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Cadastrar Nova Oficina</h3>
                     <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                       <input className="input-field" style={{ margin: 0 }} placeholder="Nome da Oficina / Razão Social" value={newTenantName} onChange={e => setNewTenantName(e.target.value)} />
                       <input className="input-field" style={{ margin: 0 }} placeholder="CPF ou CNPJ do Dono" value={newDocument} onChange={e => setNewDocument(e.target.value)} />
                       <input className="input-field" style={{ margin: 0 }} placeholder="WhatsApp (Formato: 5511999999999)" value={newPhone} onChange={e => setNewPhone(e.target.value)} />
                       <input className="input-field" style={{ margin: 0 }} placeholder="Endereço Completo" value={newAddress} onChange={e => setNewAddress(e.target.value)} />
                       <input className="input-field" style={{ margin: 0 }} placeholder="Chave API Inteligência Artificial (Própria)" value={newAiApiKey} onChange={e => setNewAiApiKey(e.target.value)} />
                       <input type="number" className="input-field" style={{ margin: 0 }} placeholder="Taxa de Implementação (R$)" value={newImplementationFee} onChange={e => setNewImplementationFee(e.target.value)} />
                       <div style={{ gridColumn: 'span 2', display: 'flex', gap: '1rem' }}>
                         <button className="btn-primary" onClick={createTenant} style={{ flex: 1 }}>Finalizar Onboarding e Criar Oficina</button>
                         <button className="btn-secondary" style={{ margin: 0, flex: 1 }} onClick={() => setIsModaling(false)}>Cancelar</button>
                       </div>
                     </div>
                  </div>
                )}
               <div className="table-wrapper">
                 <table>
                   <thead>
                     <tr>
                       <th>Tenant ID</th>
                       <th>Nome da Oficina</th>
                       <th>Cadastro</th>
                       <th>Plano SaaS</th>
                       <th>Integrações</th>
                       <th>Ações Administrativas</th>
                     </tr>
                   </thead>
                   <tbody>
                      {tenants.map(tenant => (
                        <tr key={tenant[0]} style={{ opacity: tenant[3] ? 1 : 0.6 }}>
                          <td style={{ color: 'var(--text-secondary)' }}>#{tenant[0]}</td>
                          <td style={{ fontWeight: '600', color: tenant[3] ? 'var(--primary)' : 'var(--text-secondary)' }}>
                            {tenant[1]} {!tenant[3] && '(SUSPENSO)'}
                          </td>
                          <td>{new Date(tenant[2]).toLocaleDateString()}</td>
                          <td>
                            <span style={{ color: tenant[3] ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                              ● {tenant[3] ? 'Premium (R$ 150/m)' : 'Suspenso'}
                            </span>
                          </td>
                          <td><span style={{ fontSize: '0.75rem', background: 'var(--primary-light)', color: 'var(--primary)', padding: '4px 10px', borderRadius: '6px', fontWeight: '700' }}>WHATSAPP AI</span></td>
                          <td>
                            <button 
                              onClick={() => handleImpersonate(tenant[0])}
                              disabled={!tenant[3]}
                              style={{ background: 'transparent', border: 'none', cursor: tenant[3] ? 'pointer' : 'not-allowed', color: tenant[3] ? 'var(--primary)' : 'var(--text-secondary)', fontWeight: 'bold', marginRight: '10px' }}>LOGAR COMO ADMIN</button>
                            <button 
                              onClick={() => handleToggleTenantStatus(tenant[0], tenant[3])}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: tenant[3] ? '#ef4444' : '#10b981', fontWeight: 'bold' }}>
                              {tenant[3] ? 'SUSPENDER' : 'ATIVAR'}
                            </button>
                          </td>
                        </tr>
                      ))}
                   </tbody>
                 </table>
               </div>
             </div>
          </div>
        );
      case 'billing':
        return <AdminBilling />;
      case 'saas_settings':
        return <AdminSettings />;
      case 'jev':
        return <JevVeicular currentTenant={status?.tenant || { id: tenantId }} />;
      default:
        return <div>Selecione um módulo</div>;
    }
  };

  return (
    <div className="dashboard-container">
      <aside className="sidebar">
        <div className="logo-section">
          <div className="logo-circle">SF</div>
          <div>
            <div className="logo-text">StackFab</div>
            <div style={{ fontSize: '0.7rem', fontWeight: '500', color: 'var(--text-secondary)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '-2px', paddingLeft: '2px' }}>Oficinas</div>
          </div>
        </div>
        
        <ul className="nav-menu">
          {role !== 'super_admin' && (
            <>
              <li className={`nav-item ${view === 'dashboard' ? 'active' : ''}`} onClick={() => setView('dashboard')}>
                <span>📊</span> Dashboard
              </li>
              <li className={`nav-item ${view === 'calendar' ? 'active' : ''}`} onClick={() => setView('calendar')}>
                <span>📅</span> Agenda
              </li>
              <li className={`nav-item ${view === 'jev' ? 'active' : ''}`} onClick={() => setView('jev')}>
                <span>📋</span> JEV Veicular
              </li>
              <li className={`nav-item ${view === 'customers' ? 'active' : ''}`} onClick={() => setView('customers')}>
                <span>👥</span> Clientes
              </li>
              <li className={`nav-item ${view === 'vehicles' ? 'active' : ''}`} onClick={() => setView('vehicles')}>
                <span>🚗</span> Veículos
              </li>
              <li className={`nav-item ${view === 'services' ? 'active' : ''}`} onClick={() => setView('services')}>
                <span>🛠️</span> Catálogo
              </li>
              <li className={`nav-item ${view === 'settings' ? 'active' : ''}`} onClick={() => setView('settings')}>
                <span>⚙️</span> Ajustes
              </li>
            </>
          )}

          {role === 'super_admin' && (
            <>
              <li className={`nav-item ${view === 'super_admin' ? 'active' : ''}`} onClick={() => setView('super_admin')} style={{ borderTop: '1px solid var(--border)', marginTop: '1rem', paddingTop: '1rem' }}>
                <span>🚀</span> Visão Geral (SaaS)
              </li>
              <li className={`nav-item ${view === 'billing' ? 'active' : ''}`} onClick={() => setView('billing')}>
                <span>💳</span> Faturamento / MRR
              </li>
              <li className={`nav-item ${view === 'saas_settings' ? 'active' : ''}`} onClick={() => setView('saas_settings')}>
                <span>⚙️</span> Config. Plataforma
              </li>
            </>
          )}

          <li
            style={{ marginTop: 'auto' }}
            className="nav-item"
            onClick={async () => {
              await apiFetch('/auth/logout', { method: 'POST' });
              setRole(null);
              setTenantId(null);
              setStatus(null);
              setDashboardData(null);
              setDashboardAppointments([]);
              setView('dashboard');
            }}
          >
            <span>🚪</span> Logout
          </li>
        </ul>
      </aside>

      <main className="main-content">
        <header className="header">
          <div className="welcome-section">
            <h1>{role === 'super_admin' ? 'StackFab - SaaS Admin' : (status?.tenant?.name || 'Minha Oficina')}</h1>
            <p className="stat-label">
              {role === 'super_admin' ? 'Controle global de oficinas' : 'Gestão Inteligente Automotiva'}
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button
              onClick={toggleTheme}
              title={theme === 'light' ? 'Modo noturno' : 'Modo claro'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                padding: '0.45rem 0.85rem',
                borderRadius: '40px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: '600',
                letterSpacing: '0.01em',
                border: theme === 'dark'
                  ? '1px solid rgba(59,130,246,0.25)'
                  : '1px solid var(--border)',
                background: theme === 'dark'
                  ? 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(139,92,246,0.08))'
                  : 'var(--bg-card)',
                color: theme === 'dark' ? '#60a5fa' : 'var(--text-secondary)',
                boxShadow: theme === 'dark'
                  ? '0 0 12px rgba(59,130,246,0.18), inset 0 1px 0 rgba(255,255,255,0.04)'
                  : 'var(--shadow-sm)',
                transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
              }}
            >
              <span style={{ fontSize: '1rem', lineHeight: 1 }}>
                {theme === 'light' ? '🌙' : '☀️'}
              </span>
              <span style={{ userSelect: 'none' }}>
                {theme === 'light' ? 'Night' : 'Light'}
              </span>
            </button>
            <div className="stat-card" style={{ padding: '0.6rem 1.2rem', borderRadius: '14px', display: 'flex', gap: '0.8rem', alignItems: 'center', border: '1px solid var(--border)' }}>
               <span style={{ color: '#10b981' }}>●</span> 
               {role === 'super_admin' ? (
                 <span style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '0.9rem' }}>Sistema Online</span>
               ) : (
                 <a href={`/site/${tenantId}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: '700', fontSize: '0.9rem' }}>
                   Site Público
                 </a>
               )}
            </div>
          </div>
        </header>

        {renderModule()}
      </main>
    </div>
  );
}

export default App;
